import { getQuizResultsTexts } from "./quiz-results-texts.js";

const ADDON_DISCOUNT = 15;

/**
 * @param {string | number} value
 * @returns {number}
 */
function roundToTwo(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * @param {string | number} value
 * @returns {string}
 */
function formatPrice(value) {
  const num = roundToTwo(value);
  return `$${num % 1 === 0 ? num : num.toFixed(2)}`;
}

/**
 * @param {any} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/**
 * @param {any} addon
 * @param {boolean} isOtpOn
 * @returns {number}
 */
export function getAddonPrice(addon, isOtpOn) {
  const fullPrice = Number(addon?.price || 0);
  const discount = isOtpOn ? 0 : ADDON_DISCOUNT;
  const localPrice = fullPrice * (1 - discount / 100);
  return roundToTwo(localPrice);
}

/**
 * @param {any[]} visibleAddons
 * @param {any[]} recommendedAddons
 * @returns {any[]}
 */
export function getSortedAddons(visibleAddons, recommendedAddons) {
  const list = Array.isArray(visibleAddons) ? [...visibleAddons] : [];
  const recommendedVariantIds = new Set(
    (Array.isArray(recommendedAddons) ? recommendedAddons : [])
      .map((item) => Number(item?.variant?.id))
      .filter((id) => Number.isFinite(id)),
  );

  list.sort((a, b) => {
    const aVariantId = Number(a?.variant?.id);
    const bVariantId = Number(b?.variant?.id);
    const isRecommendedA = recommendedVariantIds.has(aVariantId);
    const isRecommendedB = recommendedVariantIds.has(bVariantId);

    if (isRecommendedA && !isRecommendedB) return -1;
    if (!isRecommendedA && isRecommendedB) return 1;

    return Number(b?.variant?.price || 0) - Number(a?.variant?.price || 0);
  });

  return list;
}

/**
 * @param {Element | null} rootElement
 * @param {(variantId: number, isSelected: boolean) => void} onToggle
 * @returns {() => void}
 */
export function bindAddonsBlock(rootElement, onToggle) {
  if (!rootElement || typeof onToggle !== "function") return () => {};

  const handleChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLInputElement | null} */ (event.target);
    if (!target || !target.matches("[data-addon-checkbox]")) return;

    const variantId = Number(target.getAttribute("data-addon-variant-id"));
    if (!Number.isFinite(variantId)) return;

    onToggle(variantId, target.checked);
  };

  rootElement.addEventListener("change", handleChange);
  return () => {
    rootElement.removeEventListener("change", handleChange);
  };
}

/**
 * @param {any} addon
 * @param {boolean} isSelected
 * @param {boolean} isOtpOn
 * @returns {string}
 */
function getAddonItemHtml(addon, isSelected, isOtpOn) {
  const texts = getQuizResultsTexts();
  const variantId = Number(addon?.variant?.id);
  const image = addon?.variant?.image || addon?.media?.[0]?.src || "";
  const title = escapeHtml(addon?.title || addon?.variant?.title || "");
  const description = addon?.variant?.description || "";
  const fullPrice = Number(addon?.variant?.price || 0);
  const discountedPrice = getAddonPrice(addon?.variant, isOtpOn);
  const showComparePrice = discountedPrice < fullPrice;
  const inputId = `quiz-results-addon-${variantId}`;

  return `
    <div class="quiz-results-addons-item">
      <input
        class="quiz-results-addons-item-input"
        type="checkbox"
        id="${escapeHtml(inputId)}"
        ${isSelected ? "checked" : ""}
        data-addon-checkbox
        data-addon-variant-id="${variantId}"
      >
      <label class="quiz-results-addons-item-label" for="${escapeHtml(inputId)}">
        <span class="quiz-results-addons-item-image">
          <image-zoom
            thumb-src="${escapeHtml(image)}"
            full-src="${escapeHtml(image)}"
            mobile-src="${escapeHtml(image)}"
            alt="${title}">
          </image-zoom>
        </span>
        <span class="quiz-results-addons-item-content">
          <span class="quiz-results-addons-item-content-inner">
            <span class="quiz-results-addons-item-title">${title}</span>
            <span class="quiz-results-addons-item-price">
              <span>${discountedPrice === 0 ? texts.addons.freeLabel : formatPrice(discountedPrice)}</span>
              ${showComparePrice ? `<s>${formatPrice(fullPrice)}</s>` : ""}
            </span>
            <span class="quiz-results-addons-item-text">${description}</span>
          </span>
          <span class="quiz-results-addons-item-checkbox-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="20" width="20" role="presentation" fill="#000">
              <path d="M21 6.285l-11.16 12.733-6.84-6.018 1.319-1.49 5.341 4.686 9.865-11.196 1.475 1.285z"/>
            </svg>
          </span>
        </span>
      </label>
    </div>
  `.trim();
}

/**
 * @param {Element | null} rootElement
 * @param {{
 *   addons: any[],
 *   selectedAddonIds: number[],
 *   isOtpOn: boolean,
 *   trialDiscountValue: number
 * }} state
 */
export function renderAddonsBlock(rootElement, state) {
  if (!rootElement) return;
  const texts = getQuizResultsTexts();

  const addons = Array.isArray(state?.addons) ? state.addons : [];
  const selectedAddonIds = new Set(
    (Array.isArray(state?.selectedAddonIds) ? state.selectedAddonIds : []).map(Number),
  );
  const isOtpOn = Boolean(state?.isOtpOn);
  const trialDiscountValue = Number(state?.trialDiscountValue || 0);

  const subtitle = rootElement.querySelector("[data-addons-subtitle]");
  const checkpoints = rootElement.querySelector("[data-addons-checkpoints]");
  const checkpointItems = Array.from(
    rootElement.querySelectorAll("[data-addons-checkpoint-item]"),
  );
  const list = rootElement.querySelector("[data-addons-list]");

  if (subtitle) {
    subtitle.textContent = isOtpOn
      ? texts.addons.subtitleOtpOn
      : texts.addons.subtitleDefault;
  }

  if (checkpoints) {
    checkpoints.classList.toggle("is-hidden", isOtpOn);
    checkpoints.classList.remove("active-half", "active-full");

    if (!isOtpOn) {
      if (selectedAddonIds.size >= 1) {
        checkpoints.classList.add("active-full");
      } else {
        // checkpoints.classList.add("active-half");
      }
    }
  }

  checkpointItems.forEach((item) => {
    const itemsNumber = Number(item.getAttribute("data-items-number") || 0);
    const isPassed = selectedAddonIds.size >= itemsNumber;
    item.classList.toggle("active", isPassed);
  });

  const trialDiscountElement = rootElement.querySelector("[data-addons-trial-discount]");
  if (trialDiscountElement) {
    trialDiscountElement.textContent = `${trialDiscountValue}${texts.addons.trialDiscountSuffix}`;
  }

  if (!list) return;
  if (!addons.length) {
    list.innerHTML = "";
    return;
  }

  list.innerHTML = addons
    .map((addon) =>
      getAddonItemHtml(addon, selectedAddonIds.has(Number(addon?.variant?.id)), isOtpOn),
    )
    .join("");
}
